import { createI18n, type Composer } from 'vue-i18n';
import { COUNTRY_CODE } from './utils/constants';

/**
 * Setup the global instance.
 * Default to 'en' (AU) as the fallback for missing keys in other regions.
 */
export const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  flatJson: true, // Allow flat keys like "module.feature.key"
  messages: {
    en: {}, // Start empty, each modules will populate this
  },
});

/**
 * Set the locale dynamically based on the country code.
 * Default to 'en' which has AU content.
 */
export function setLocaleByCountryCode(countryCode: COUNTRY_CODE) {
  switch (countryCode) {
    case COUNTRY_CODE['United Kingdom']:
      setLocale('en-GB');
      break;
    default:
      setLocale('en');
      break;
  }
}

function setLocale(locale: string) {
  const i18nGlobal = i18n.global as unknown as Composer;

  if (i18nGlobal.locale.value !== locale) {
    i18nGlobal.locale.value = locale;
    document.querySelector('html')?.setAttribute('lang', locale);
  }
}

/**
 * Helper for lazy-loading module translations. Take the pre-globbed locale files and load them into the i18n global instance.
 * @param localeFiles - The locale files from import.meta.glob
 */
export function loadLocaleFiles(localeFiles: Record<string, unknown>) {
  const messages = Object.entries(localeFiles).reduce((acc, [filepath, content]) => {
    const locale = getLocaleFromFilepath(filepath);
    return locale ? { ...acc, [locale]: content } : acc;
  }, {});

  mergeModuleMessages(messages);
}

/**
 * Extract the locale from the filepath
 *
 * Eg: "src/modules/commissioning/locales/en-GB.json" -> "en-GB"
 * @param filepath
 * @returns the locale code
 */
function getLocaleFromFilepath(filepath: string) {
  return filepath.match(/\.\/(.*)\.json$/)?.[1] ?? null;
}

/**
 * Merge the locale messages into the i18n global instance.
 * @param messages - The JSON object containing translations
 */
function mergeModuleMessages(messages: Record<string, unknown>) {
  Object.keys(messages).forEach((locale) => {
    i18n.global.mergeLocaleMessage(locale, messages[locale]);
  });
}
